import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { type Mode, resolveMode } from "./resolveMode";

/** localStorage key for the persisted theme choice. */
export const THEME_KEY = "fw-theme";

/** What the person picked: a mode, or "follow the device" when nothing is stored. */
export type ThemeChoice = Mode | "auto";

interface ThemeValue {
  mode: Mode;
  choice: ThemeChoice;
  setMode: (m: Mode) => void;
  setChoice: (c: ThemeChoice) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

const LIGHT_QUERY = "(prefers-color-scheme: light)";

function deviceIsLight(): boolean {
  return window.matchMedia?.(LIGHT_QUERY).matches ?? false;
}

function initialMode(): Mode {
  return resolveMode(localStorage.getItem(THEME_KEY), deviceIsLight());
}

function initialChoice(): ThemeChoice {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === "light" || stored === "dark" ? stored : "auto";
}

/**
 * Owns the one theme axis (mode). Neutrals live in CSS keyed off `data-theme`;
 * this provider only flips that attribute on <html>, honors the OS preference on
 * first load (and keeps following it while nothing is picked), and persists the
 * choice to localStorage.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(initialMode);
  const [choice, setChoiceState] = useState<ThemeChoice>(initialChoice);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  useEffect(() => {
    if (choice !== "auto") return;
    const mq = window.matchMedia?.(LIGHT_QUERY);
    if (!mq?.addEventListener) return;
    const follow = () => setModeState(mq.matches ? "light" : "dark");
    mq.addEventListener("change", follow);
    return () => mq.removeEventListener("change", follow);
  }, [choice]);

  const setMode = useCallback((m: Mode) => {
    localStorage.setItem(THEME_KEY, m);
    setChoiceState(m);
    setModeState(m);
  }, []);

  const setChoice = useCallback(
    (c: ThemeChoice) => {
      if (c !== "auto") {
        setMode(c);
        return;
      }
      localStorage.removeItem(THEME_KEY);
      setChoiceState("auto");
      setModeState(deviceIsLight() ? "light" : "dark");
    },
    [setMode],
  );

  const toggle = useCallback(() => setMode(mode === "dark" ? "light" : "dark"), [mode, setMode]);

  const value = useMemo<ThemeValue>(
    () => ({ mode, choice, setMode, setChoice, toggle }),
    [mode, choice, setMode, setChoice, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
